import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402';

export const adminService = {
  async getStats() {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/admin/stats`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as {
      users: number;
      pendingCreators: number;
      totalPosts: number;
      flaggedPosts: number;
      totalRevenue: number;
      failedPayments: number;
    };
  },
  async listUsers(params: { query?: string; role?: 'admin' | 'creator' | 'patron' | 'all'; status?: 'active' | 'suspended' | 'all'; limit?: number; offset?: number } = {}) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/admin/users`, {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return res.data as { users: any[]; pagination: any };
  },

  async suspendUser(userId: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/admin/users/${userId}/suspend`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  async activateUser(userId: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/admin/users/${userId}/activate`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  async setAdmin(userId: string, isAdmin: boolean) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/admin/users/${userId}/role`, { isAdmin }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  async listPendingCreators() {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/admin/creators/pending`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { creators: any[] };
  },

  async verifyCreator(creatorProfileId: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/admin/creators/${creatorProfileId}/verify`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  async getFlags() {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/admin/flags`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { flags: { adminConsole: boolean; reports: boolean } };
  },

  async listCoupons() {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/admin/coupons`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { coupons: any[] };
  },

  async createCoupon(data: {
    code: string;
    description?: string;
    percentOff?: number;
    amountOff?: number;
    currency?: string;
    maxRedemptions?: number;
    redeemBy?: string;
    active?: boolean;
  }) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/admin/coupons`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { coupon: any };
  },

  async updateCoupon(id: string, data: {
    code?: string;
    description?: string;
    percentOff?: number;
    amountOff?: number;
    currency?: string;
    maxRedemptions?: number;
    redeemBy?: string;
    active?: boolean;
  }) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/admin/coupons/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { coupon: any };
  },

  async listKYCDocuments(params: { status?: 'PENDING' | 'APPROVED' | 'REJECTED' } = {}) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/kyc/admin`, {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return res.data as { documents: any[] };
  },

  async updateKYCStatus(documentId: string, status: 'PENDING' | 'APPROVED' | 'REJECTED', note?: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/kyc/${documentId}/status`, { status, note }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { document: any };
  },

  async listPayouts() {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/payouts/admin`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { payouts: any[] };
  },

  async updatePayoutStatus(payoutId: string, status: 'PROCESSING' | 'COMPLETED' | 'FAILED', failureReason?: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/payouts/${payoutId}/status`, { status, failureReason }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { payout: any };
  },

  // New methods for enhanced admin features
  async shadowbanUser(userId: string, reason: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/admin/users/${userId}/shadowban`, { reason }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  async unshadowbanUser(userId: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/admin/users/${userId}/unshadowban`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  async setModerator(userId: string, isModerator: boolean) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/admin/users/${userId}/moderator`, { isModerator }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  async setSupport(userId: string, isSupport: boolean) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/admin/users/${userId}/support`, { isSupport }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  async getAuditLogs(params: { action?: string; targetType?: string; userId?: string; limit?: number; offset?: number } = {}) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/admin/audit-logs`, {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return res.data as { logs: any[]; pagination: any };
  },

  async getDisputes(params: { status?: string; type?: string; limit?: number; offset?: number } = {}) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/admin/disputes`, {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return res.data as { disputes: any[]; pagination: any };
  },

  async assignDispute(disputeId: string, assignToId: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/admin/disputes/${disputeId}/assign`, { assignToId }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  async resolveDispute(disputeId: string, resolution: string, outcome: 'UPHELD' | 'DENIED' | 'PARTIAL') {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/admin/disputes/${disputeId}/resolve`, { resolution, outcome }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  async createAnnouncement(data: {
    title: string;
    content: string;
    type: 'INFO' | 'WARNING' | 'ERROR' | 'SUCCESS' | 'FEATURE' | 'MAINTENANCE';
    targetRoles?: string[];
    startDate?: string;
    endDate?: string;
  }) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/admin/announcements`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { announcement: any };
  },

  async getAnnouncements(params: { isActive?: boolean } = {}) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/admin/announcements`, {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return res.data as { announcements: any[] };
  },

  async updateAnnouncement(id: string, data: {
    title?: string;
    content?: string;
    type?: string;
    targetRoles?: string[];
    startDate?: string;
    endDate?: string;
  }) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/admin/announcements/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { announcement: any };
  },

  async getPlatformConfig(params: { category?: string } = {}) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/admin/config`, {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return res.data as { config: any[] };
  },

  async updatePlatformConfig(data: {
    key: string;
    value: string;
    category?: string;
    description?: string;
    isPublic?: boolean;
  }) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/admin/config`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { config: any };
  },

  async approveKYC(kycId: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/admin/kyc/${kycId}/approve`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  async rejectKYC(kycId: string, reason: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/admin/kyc/${kycId}/reject`, { reason }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  // API Keys Management
  async getApiKeys() {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/admin/api-keys`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as Array<{
      key: string;
      description: string;
      isConfigured: boolean;
      maskedValue: string | null;
      updatedAt: Date;
    }>;
  },

  async updateApiKey(key: string, value: string, description?: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.put(`${API_BASE_URL}/api/admin/api-keys/${key}`, { value, description }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  async deleteApiKey(key: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.delete(`${API_BASE_URL}/api/admin/api-keys/${key}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  },

  async testApiKey(key: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.post(`${API_BASE_URL}/api/admin/api-keys/test/${key}`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data as { success: boolean; message: string };
  },

  async decryptApiKey(key: string) {
    const token = localStorage.getItem('accessToken');
    const res = await axios.get(`${API_BASE_URL}/api/admin/api-keys/decrypt/${key}`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { confirm: 'true' },
    });
    return res.data as { key: string; value: string; warning: string };
  },
};


